import React, { useRef, useState, useEffect, useCallback } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useDesignSystem, useId } from "@xsolla/xui-core";
import { Clock } from "@xsolla/xui-icons-base";
import { TimeSegment } from "./TimeSegment";
import { PeriodToggle } from "./PeriodToggle";
import { clampHours, clampMinutes, clampSeconds } from "./utils";
import type { InputTimeProps, TimeValue } from "./types";

export const InputTime: React.FC<InputTimeProps> = ({
  value,
  onChange,
  showSeconds = false,
  showPeriod = false,
  hourCycle = 24,
  icon = <Clock />,
  size = "md",
  disabled = false,
  error,
  "aria-label": ariaLabel,
  testID,
}) => {
  const { theme } = useDesignSystem();
  const rawId = useId();
  const safeId = rawId.replace(/:/g, "");
  const inputId = `input-time-${safeId}`;
  const errorId = `${inputId}-error`;

  const [internalState, setInternalState] = useState<"default" | "focus">(
    "default"
  );

  const segmentRefs = useRef<Array<{ focus: () => void }>>([]);

  const is12h = hourCycle === 12;

  const [hours, setHours] = useState<number | null>(value?.hours ?? null);
  const [minutes, setMinutes] = useState<number | null>(value?.minutes ?? null);
  const [seconds, setSeconds] = useState<number | null>(value?.seconds ?? null);
  const [period, setPeriod] = useState<"am" | "pm">(value?.period ?? "am");

  useEffect(() => {
    if (value) {
      setHours(value.hours);
      setMinutes(value.minutes);
      setSeconds(value.seconds ?? null);
      setPeriod(value.period ?? "am");
    } else if (value === null) {
      setHours(null);
      setMinutes(null);
      setSeconds(null);
      setPeriod("am");
    }
  }, [value]);

  const emitChange = useCallback(
    (h: number | null, m: number | null, s: number | null, p: "am" | "pm") => {
      if (h === null || m === null) {
        onChange?.(null);
        return;
      }
      const result: TimeValue = {
        hours: clampHours(h, is12h),
        minutes: clampMinutes(m),
      };
      if (showSeconds) {
        result.seconds = s !== null ? clampSeconds(s) : 0;
      }
      if (showPeriod) {
        result.period = p;
      }
      onChange?.(result);
    },
    [onChange, is12h, showSeconds, showPeriod]
  );

  const handleHoursChange = (v: number | null) => {
    const clamped = v !== null ? clampHours(v, is12h) : null;
    setHours(clamped);
    emitChange(clamped, minutes, seconds, period);
  };

  const handleMinutesChange = (v: number | null) => {
    const clamped = v !== null ? clampMinutes(v) : null;
    setMinutes(clamped);
    emitChange(hours, clamped, seconds, period);
  };

  const handleSecondsChange = (v: number | null) => {
    const clamped = v !== null ? clampSeconds(v) : null;
    setSeconds(clamped);
    emitChange(hours, minutes, clamped, period);
  };

  const handlePeriodChange = (v: "am" | "pm") => {
    setPeriod(v);
    emitChange(hours, minutes, seconds, v);
  };

  const focusSegment = (index: number) => {
    segmentRefs.current[index]?.focus?.();
  };

  const segmentCount = showSeconds ? 3 : 2;
  const advanceFrom = (i: number) => () => {
    if (i < segmentCount - 1) focusSegment(i + 1);
  };
  const retreatFrom = (i: number) => () => {
    if (i > 0) focusSegment(i - 1);
  };

  const sizeStyles = theme.sizing.input(size);
  const inputColors = theme.colors.control.input;

  const isError = !!error;
  const isFocus = internalState === "focus";

  let backgroundColor = inputColors.bg;
  let borderColor = inputColors.border;
  let outlineColor: string | undefined;

  if (disabled) {
    backgroundColor = inputColors.bgDisable;
    borderColor = inputColors.borderDisable;
  } else if (isError) {
    outlineColor = theme.colors.border.alert;
    if (isFocus) {
      backgroundColor = theme.colors.control.focus.bg;
    }
  } else if (isFocus) {
    backgroundColor = theme.colors.control.focus.bg;
    outlineColor = theme.colors.border.brand;
  }

  const textColor = disabled ? inputColors.textDisable : inputColors.text;
  const placeholderColor = inputColors.placeholder;
  const iconColor = inputColors.placeholder;

  const paddingConfig: Record<
    string,
    { vertical: number; horizontal: number }
  > = {
    xl: { vertical: 12, horizontal: 12 },
    lg: { vertical: 14, horizontal: 12 },
    md: { vertical: 11, horizontal: 12 },
    sm: { vertical: 7, horizontal: 10 },
    xs: { vertical: 7, horizontal: 10 },
  };

  const iconSizeConfig: Record<string, number> = {
    xl: 22,
    lg: 20,
    md: 18,
    sm: 16,
    xs: 14,
  };

  const focusOutlineConfig: Record<string, { width: number; offset: number }> =
    {
      xl: { width: 1, offset: -1 },
      lg: { width: 1, offset: -1 },
      md: { width: 1, offset: -1 },
      sm: { width: 1, offset: -1 },
      xs: { width: 1, offset: -1 },
    };

  const padding = paddingConfig[size];
  const borderRadius = theme.shape.input[size].borderRadius;
  const iconSize = iconSizeConfig[size];
  const focusOutline = focusOutlineConfig[size];

  const handleContainerFocus = () => {
    if (!disabled) setInternalState("focus");
  };
  const handleContainerBlur = () => {
    if (!disabled) setInternalState("default");
  };

  const colonColor = disabled ? placeholderColor : textColor;

  return (
    <Box
      flexDirection="column"
      gap={sizeStyles.fieldGap}
      width="100%"
      testID={testID}
    >
      <Box
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderWidth={borderColor !== "transparent" ? 1 : 0}
        borderRadius={borderRadius}
        height={sizeStyles.height}
        paddingVertical={padding.vertical}
        paddingHorizontal={padding.horizontal}
        flexDirection="row"
        alignItems="center"
        gap={2}
        position="relative"
        style={{
          ...(outlineColor
            ? {
                outline: `${focusOutline.width}px solid ${outlineColor}`,
                outlineOffset: `${focusOutline.offset}px`,
              }
            : {}),
        }}
        hoverStyle={
          !disabled && !isFocus && !isError
            ? {
                backgroundColor: inputColors.bgHover,
                borderColor: inputColors.borderHover,
              }
            : undefined
        }
        onFocus={handleContainerFocus}
        onBlur={handleContainerBlur}
        role="group"
        aria-label={ariaLabel || "Time input"}
        aria-describedby={error ? errorId : undefined}
      >
        {icon && (
          <Box marginRight={6} alignItems="center" justifyContent="center">
            {React.isValidElement(icon)
              ? React.cloneElement(icon as React.ReactElement<any>, {
                  size: iconSize,
                  color: iconColor,
                })
              : icon}
          </Box>
        )}

        <TimeSegment
          ref={(el: any) => {
            segmentRefs.current[0] = el;
          }}
          value={hours}
          onChange={handleHoursChange}
          onAdvance={advanceFrom(0)}
          onRetreat={retreatFrom(0)}
          min={is12h ? 1 : 0}
          max={is12h ? 12 : 23}
          disabled={disabled}
          placeholder="--"
          fontSize={sizeStyles.fontSize}
          textColor={textColor}
          placeholderColor={placeholderColor}
          focusColor={theme.colors.background.brand.primary}
          aria-label="Hours"
          testID={testID ? `${testID}-hours` : undefined}
        />

        <Text
          color={colonColor}
          fontSize={sizeStyles.fontSize}
          fontWeight="500"
        >
          :
        </Text>

        <TimeSegment
          ref={(el: any) => {
            segmentRefs.current[1] = el;
          }}
          value={minutes}
          onChange={handleMinutesChange}
          onAdvance={advanceFrom(1)}
          onRetreat={retreatFrom(1)}
          min={0}
          max={59}
          disabled={disabled}
          placeholder="--"
          fontSize={sizeStyles.fontSize}
          textColor={textColor}
          placeholderColor={placeholderColor}
          focusColor={theme.colors.background.brand.primary}
          aria-label="Minutes"
          testID={testID ? `${testID}-minutes` : undefined}
        />

        {showSeconds && (
          <>
            <Text
              color={colonColor}
              fontSize={sizeStyles.fontSize}
              fontWeight="500"
            >
              :
            </Text>
            <TimeSegment
              ref={(el: any) => {
                segmentRefs.current[2] = el;
              }}
              value={seconds}
              onChange={handleSecondsChange}
              onAdvance={advanceFrom(2)}
              onRetreat={retreatFrom(2)}
              min={0}
              max={59}
              disabled={disabled}
              placeholder="--"
              fontSize={sizeStyles.fontSize}
              textColor={textColor}
              placeholderColor={placeholderColor}
              focusColor={theme.colors.background.brand.primary}
              aria-label="Seconds"
              testID={testID ? `${testID}-seconds` : undefined}
            />
          </>
        )}

        {showPeriod && (
          <PeriodToggle
            value={period}
            onChange={handlePeriodChange}
            disabled={disabled}
            fontSize={sizeStyles.fontSize}
            textColor={textColor}
            testID={testID ? `${testID}-period` : undefined}
          />
        )}
      </Box>

      {error && (
        <Text
          id={errorId}
          role="alert"
          color={theme.colors.content.alert.primary}
          fontSize={sizeStyles.fontSize - 2}
        >
          {error}
        </Text>
      )}
    </Box>
  );
};

InputTime.displayName = "InputTime";
